<template>
  <div class="audio-player">
    <img src="@/assets/audio-player-bg.png" alt="">
    <div class="audio-player-content" :class="{ 'is-play': isPlay, 'is-error': isError }" @click="handlePlay">
      <!-- 错误提示（最高优先级） -->
      <span v-if="isError" class="error-tip">音频加载失败，点击重试</span>

      <!-- 播放/暂停图标（常驻渲染，不随加载状态销毁） -->
      <template v-else>
        <img v-if="!isPlay" src="@/assets/audio-player-icon.png" alt="播放" class="play-icon">
        <img v-else src="@/assets/audio-puase-icon.png" alt="暂停" class="play-icon">
      </template>

      <!-- 加载中：叠加覆盖，不替换图标；切歌中强制隐藏 -->
      <van-loading 
        v-if="!isError && !isSwitching && isLoading" 
        size="24px" 
        vertical 
        color="#fff"
        class="loading-mask"
      >
        加载中...
      </van-loading>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  data() {
    return {
      isPlay: false,
      isError: false,
      isLoading: false,
      audio: null,
      audioSrcList: [],
      currentIndex: 0,
      loadTimer: null,
      loadingShowTimer: null, // 加载显示防抖定时器
      isSwitching: false,
      playRetryCount: 0,
      maxRetry: 2
    };
  },
  created() {
    const unlockAudio = () => {
      const temp = new Audio();
      temp.play().catch(e => { });
      temp.pause();
      document.body.removeEventListener('touchstart', unlockAudio);
    };
    document.body.addEventListener('touchstart', unlockAudio, { once: true });

    const audioSrcList = sessionStorage.getItem('audioSrcList')
      ? JSON.parse(sessionStorage.getItem('audioSrcList'))
      : [];
    this.audioSrcList = audioSrcList;
    console.log('audioSrcList:', audioSrcList);
  },
  mounted() {
    this.initAudio();
  },
  beforeDestroy() {
    clearTimeout(this.loadTimer);
    clearTimeout(this.loadingShowTimer);
    this.destroyAudio();
    this.audio = null;
  },
  methods: {
    bindAudioEvents() {
      if (!this.audio) return;
      this.audio.addEventListener('play', this.handlePlayEvent);
      this.audio.addEventListener('pause', this.handlePauseEvent);
      this.audio.addEventListener('ended', this.handleEndedEvent);
      this.audio.addEventListener('error', this.handleErrorEvent);
      this.audio.addEventListener('loadstart', this.handleLoadStart);
      this.audio.addEventListener('canplay', this.handleCanPlay);
      this.audio.addEventListener('waiting', this.handleWaiting);
      this.audio.addEventListener('canplaythrough', this.handleCanPlayThrough);
    },

    unbindAudioEvents() {
      if (!this.audio) return;
      this.audio.removeEventListener('play', this.handlePlayEvent);
      this.audio.removeEventListener('pause', this.handlePauseEvent);
      this.audio.removeEventListener('ended', this.handleEndedEvent);
      this.audio.removeEventListener('error', this.handleErrorEvent);
      this.audio.removeEventListener('loadstart', this.handleLoadStart);
      this.audio.removeEventListener('canplay', this.handleCanPlay);
      this.audio.removeEventListener('waiting', this.handleWaiting);
      this.audio.removeEventListener('canplaythrough', this.handleCanPlayThrough);
    },

    destroyAudio() {
      if (!this.audio) return;
      this.unbindAudioEvents();
      try {
        this.audio.pause();
        this.audio.currentTime = 0;
      } catch (e) {
        console.warn('销毁音频异常', e);
      }
      this.audio.src = '';
    },

    initAudio() {
      if (this.audioSrcList.length === 0) {
        this.isError = true;
        this.isLoading = false;
        return;
      }

      if (!this.audio) {
        this.audio = new Audio();
        this.audio.preload = 'auto';
        this.bindAudioEvents();
      }

      this.playRetryCount = 0;
      this.isError = false;
      this.currentIndex = 0;
      this.audio.src = this.audioSrcList[0];
      this.audio.load();
    },

    // ========== 新增：加载显示防抖 ==========
    showLoading() {
      clearTimeout(this.loadingShowTimer);
      this.loadingShowTimer = setTimeout(() => {
        this.isLoading = true;
      }, 300); // 300ms内加载完成不显示loading，杜绝闪烁
    },
    hideLoading() {
      clearTimeout(this.loadingShowTimer);
      this.isLoading = false;
    },

    restartFromBeginning() {
      clearTimeout(this.loadTimer);
      this.currentIndex = 0;
      this.isError = false;
      this.isSwitching = false;
      this.showLoading();
      this.playRetryCount = 0;

      this.audio.src = this.audioSrcList[0];
      this.audio.load();
      this.safePlay(true);
    },

    safePlay(isUserOperate = false) {
      if (!this.audio) return Promise.reject('音频实例不存在');
      return this.audio.play()
        .then(() => {
          this.playRetryCount = 0;
          if (!this.isSwitching) {
            this.isPlay = true;
          }
          return true;
        })
        .catch(err => {
          console.error('音频播放Promise捕获失败', err, '用户操作：', isUserOperate);
          if (!isUserOperate && this.playRetryCount < this.maxRetry) {
            this.playRetryCount++;
            console.log(`播放失败，第${this.playRetryCount}次重试`);
            return new Promise(resolve => {
              setTimeout(() => {
                this.reloadCurrentAudio();
                resolve(this.safePlay(false));
              }, 100);
            });
          }
          this.isError = true;
          this.hideLoading();
          this.isSwitching = false;
          if (!isUserOperate) {
            this.isPlay = false;
          }
          return Promise.reject(err);
        });
    },

    reloadCurrentAudio() {
      if (!this.audio) return;
      if (!this.isSwitching) {
        try {
          this.audio.pause();
        } catch (e) {}
      }
      this.audio.src = this.audioSrcList[this.currentIndex];
      this.audio.load();
      this.showLoading();
    },

    handlePlay() {
      if (this.isError) {
        this.restartFromBeginning();
        return;
      }
      if (!this.audio || this.isSwitching || this.isLoading) return;

      if (this.isPlay) {
        this.audio.pause();
        this.isPlay = false;
      } else {
        if (this.audio.ended && this.currentIndex === this.audioSrcList.length - 1) {
          this.restartFromBeginning();
          return;
        }

        if (this.audio.readyState === 0) {
          this.showLoading();
        }
        this.safePlay(true);
      }
    },

    handlePlayEvent() {
      if (this.isSwitching) return;
      this.isPlay = true;
      this.isError = false;
    },
    handlePauseEvent() {
      if (this.isSwitching) return;
    },

    handleEndedEvent() {
      console.log('当前音频播放结束，索引：', this.currentIndex);
      const nextIndex = this.currentIndex + 1;

      // 切歌加锁：isPlay锁死为true，isLoading不显示
      this.isSwitching = true;
      this.isPlay = true;
      this.hideLoading(); // 切歌全程隐藏loading，彻底杜绝状态切换闪烁
      this.playRetryCount = 0;

      if (nextIndex < this.audioSrcList.length) {
        this.currentIndex = nextIndex;
        this.isError = false;

        this.audio.src = this.audioSrcList[nextIndex];
        this.audio.load();

        this.safePlay(false)
          .then(() => {
            this.isSwitching = false;
          })
          .catch(err => {
            console.error('下一首播放失败:', err);
            this.isError = true;
            this.isPlay = false;
            this.hideLoading();
            this.isSwitching = false;
          });
      } else {
        this.isPlay = false;
        this.hideLoading();
        this.isSwitching = false;
        console.log('全部音频播放完成');
      }
    },

    handleErrorEvent(e) {
      if (this.isSwitching) return;
      console.error('音频加载失败，错误详情:', e);
      this.isError = true;
      this.isPlay = false;
      this.hideLoading();
      clearTimeout(this.loadTimer);
    },

    handleLoadStart() {
      if (this.isSwitching) return;
      this.showLoading();
    },
    handleCanPlay() {
      if (this.isSwitching) return;
      console.log('音频可播放');
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => {
        this.hideLoading();
      }, 300);
    },
    handleWaiting() {
      if (this.isSwitching) return;
      console.log('音频缓冲卡顿');
      this.showLoading();
    },
    handleCanPlayThrough() {
      if (this.isSwitching) return;
      console.log('音频可流畅播放');
      clearTimeout(this.loadTimer);
      this.hideLoading();
    }
  }
};
</script>

<style lang="scss" scoped>
.audio-player {
  width: 100%;
  position: relative;

  img {
    width: 100%;
  }

  .audio-player-content {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .play-icon {
      width: 16vw;
      height: 16vw;
    }

    // 加载层：绝对定位居中覆盖，不替换图标节点
    .loading-mask {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.4);
      padding: 8px 12px;
      border-radius: 8px;
    }

    &.is-play {
      background: transparent;
    }

    &.is-error {
      cursor: pointer;

      .error-tip {
        color: #fff;
        font-size: 14px;
      }
    }

    /deep/ .van-loading__text {
      color: #fff;
      font-size: 12px;
      margin-top: 6px;
    }
  }
}
</style>